import React from 'react';
import { LinkIcon } from '@heroicons/react/24/outline';
import { Establishment } from '../EstablishmentTypes';
import { EnhancedEstablishmentCell, DateCell, NafCodeCell, SizeCell, SiegeCell } from './TableCells';
import { LinkedAdherent } from '../../../store/slices/nouveauxAdherentsSlice';
import { useTableUtils } from './useTableUtils';

interface NonClientFromClientRowProps {
  establishment: Establishment;
  utils: ReturnType<typeof useTableUtils>;
  handleShowLinkedAdherents: (adherents: LinkedAdherent[]) => void;
}

export const NonClientFromClientRow: React.FC<NonClientFromClientRowProps> = ({ 
  establishment, 
  utils, 
  handleShowLinkedAdherents 
}) => (
  <>
    <EnhancedEstablishmentCell 
      establishment={establishment} 
      copyToClipboard={utils.copyToClipboard} 
      openGoogleSearch={utils.openGoogleSearch}
    />
    <DateCell date={establishment.creationDate} />
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      {establishment.linkedAdherents && establishment.linkedAdherents.length > 0 ? (
        establishment.linkedAdherents.length === 1 ? (
          // Direct display for a single adherent
          <div className="border border-blue-200 rounded-md p-3 bg-blue-50">
            <div className="font-medium text-gray-900">{establishment.linkedAdherents[0].name}</div>
            {establishment.linkedAdherents[0].address && (
              <div className="text-xs text-gray-500 mt-1">
                {[
                  establishment.linkedAdherents[0].address.numeroVoie,
                  establishment.linkedAdherents[0].address.typeVoie, 
                  establishment.linkedAdherents[0].address.libelleVoie
                ].filter(Boolean).join(' ')}
                {(establishment.linkedAdherents[0].address.numeroVoie || 
                  establishment.linkedAdherents[0].address.typeVoie || 
                  establishment.linkedAdherents[0].address.libelleVoie) && 
                  (establishment.linkedAdherents[0].address.postalCode || 
                    establishment.linkedAdherents[0].address.city) && ', '}
                <br/>
                {[
                  establishment.linkedAdherents[0].address.postalCode, 
                  establishment.linkedAdherents[0].address.city
                ].filter(Boolean).join(' ')}
              </div>
            )}
            <a 
              href={`/adherents/${establishment.linkedAdherents[0].id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 mt-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Voir l'adhérent
            </a>
          </div>
        ) : (
          // Button for multiple adherents
          <button
            onClick={() => handleShowLinkedAdherents(establishment.linkedAdherents!)}
            className="inline-flex items-center rounded-md bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            <LinkIcon className="h-4 w-4 mr-1.5" />
            {`${establishment.linkedAdherents.length} adhérents liés`}
          </button>
        )
      ) : (
        <span className="inline-flex items-center text-gray-500 px-3 py-2 text-sm">
          <svg className="h-4 w-4 mr-1.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          Aucun adhérent lié
        </span>
      )}
    </td>
    <NafCodeCell nafCode={establishment.nafCode} getNafCodeLabel={utils.getNafCodeLabel} />
    <SizeCell trancheEffectifs={establishment.trancheEffectifs} categorieEntreprise={establishment.categorieEntreprise} />
    <SiegeCell 
      siege={establishment.siege} 
      getNafCodeLabel={utils.getNafCodeLabel} 
      copyToClipboard={utils.copyToClipboard} 
    />
  </>
); 